import { DateTime } from 'luxon';

const predefinedColors = {
    idle: '#9ca3af', // neutral gray
    self_consumption: '#3b82f6', // blue
    peak_shaving: '#f59e0b', // amber
    target_soc: '#10b981', // green
    fcr_d: '#ef4444', // red
    fcr_d_down: '#8b5cf6',
    fcr_d_up: '#46e3f9',
    fcr_n: '#673414',
    direct_command: '#b53153',
};

export function generateColorFromId(label) {
    if (predefinedColors[label]) {
        return predefinedColors[label];
    }

    return '#d1d5db';
}

export const sanitizeModeName = name => {
    const sanitized = name.replace(/[^a-zA-Z0-9_]/g, '_');
    return sanitized.length > 20 ? sanitized.slice(0, 20) : sanitized;
};

export const formatLabel = label => {
    const labels = {
        peak_shaving: 'Peak Shaving',
        self_consumption: 'Self Consumption',
        idle: 'Idle',
    };

    return labels[label] || label.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Turn the array of 96 'slots' into an array of blocks in your timeline format.
 * Each slot is 15 minutes, i.e. 4 slots per hour.
 *
 * Example slot shape:
 *  {
 *    mode: { id: 0, name: "idle" },
 *    slot: { id: 35 }
 *  }
 *
 * We'll group consecutive slots that share the same `mode.name`.
 */

export function convertDayBlocksToWeeklyBp(dayBlocks, referenceDate, modes) {
    const modeMap = Object.fromEntries(modes.map(mode => [mode.name, mode.id]));

    const SLOT_COUNT = 96;
    const result = {};

    Object.entries(dayBlocks).forEach(([date, blocks]) => {
        const slots = Array(SLOT_COUNT).fill(modeMap['idle']);

        blocks.forEach(block => {
            const startSlot = Math.floor(block.start * 4);
            const endSlot = Math.floor(block.end * 4);

            const modeId = modeMap[block.label];
            if (modeId === undefined) {
                throw new Error(`Mode '${block.label}' not found in modeMap`);
            }

            for (let i = startSlot; i < endSlot && i < SLOT_COUNT; i++) {
                slots[i] = modeId;
            }
        });

        const day = DateTime.fromISO(date).toFormat('cccc').toLowerCase();
        result[day] = {
            name: day,
            slots,
        };
    });

    return result;
}

export function mapWeeklyBlueprint(deviceSchedule, configByDay) {
    const result = {};

    const weeklyBp = deviceSchedule.weekly_bp;
    if (!weeklyBp) {
        return result;
    }

    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    dayNames.forEach(dayName => {
        const day = weeklyBp[dayName];
        if (!day || !day.id) {
            return;
        }

        const dailyBpId = day.id;

        const SLOTS_COUNT = 97;
        const slotsDict = {};

        if (Array.isArray(day.slots) && day.slots.length > 0) {
            for (const s of day.slots) {
                const slotIndex = s.slot.id;
                slotsDict[slotIndex] = {
                    mode: s.mode?.id ?? 0,
                    config: {},
                };
            }
            for (let i = 0; i < SLOTS_COUNT; i++) {
                if (!(i in slotsDict)) {
                    slotsDict[i] = { mode: 0, config: {} };
                }
            }
        } else {
            for (let i = 0; i < SLOTS_COUNT; i++) {
                slotsDict[i] = { mode: 0, config: {} };
            }
        }

        const dayOverrides = configByDay[dailyBpId] || [];
        for (const o of dayOverrides) {
            const from = o.from_slot;
            const to = o.to_slot;
            const id = o.id;
            for (let i = from; i <= to; i++) {
                if (i in slotsDict && slotsDict[i].mode === o.mode) {
                    slotsDict[i].config = o.config || {};
                    slotsDict[i].configId = id;
                }
            }
        }

        result[dayName] = {
            id: dailyBpId,
            slots: slotsDict,
        };
    });

    return result;
}

export const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) {
        return null;
    }

    const [startHour, startMinute] = startTime.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startHour);
    startDate.setMinutes(startMinute);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    const endDate = new Date(startDate.getTime() + (duration.hours * 60 + duration.minutes) * 60000);

    const endHours = endDate.getHours().toString().padStart(2, '0');
    const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
};

export const convertSlotsToBlocks = (slots, modes) => {
    const blocks = [];
    const modeMap = new Map(modes.map(mode => [Number(mode.id), mode.name]));

    let currentMode = null;
    let currentStart = null;
    let currentConfig = null;
    let currentConfigId = null;

    for (let i = 0; i <= 96; i++) {
        const slot = slots[i];
        const mode = Number(slot?.mode ?? 0);
        const config = slot?.config || {};
        const configId = slot?.configId ?? null;

        const modeChanged = mode !== currentMode;
        const configChanged = JSON.stringify(config) !== JSON.stringify(currentConfig);
        const configIdChanged = configId !== currentConfigId;

        if (modeChanged || configChanged || configIdChanged) {
            if (currentMode !== null) {
                blocks.push({
                    id: `${currentMode}-${currentStart}-${i - 1}-${Date.now()}`,
                    start: currentStart / 4,
                    end: i / 4,
                    duration: {
                        hours: Math.floor((i - currentStart) / 4),
                        minutes: ((i - currentStart) % 4) * 15,
                    },
                    label: modeMap.get(currentMode) || 'idle',
                    mode: currentMode,
                    config: currentConfig,
                    configId: currentConfigId,
                });
            }

            currentMode = mode;
            currentStart = i;
            currentConfig = config;
            currentConfigId = configId;
        }
    }
    return blocks;
};

export function bpSlotsToBlocks(slots) {
    const blocks = [];
    let currentBlock = null;

    for (let i = 0; i < 96; i++) {
        const slot = slots[i];
        if (!slot) {
            continue;
        }

        if (!currentBlock || currentBlock.mode !== slot.mode.id) {
            if (currentBlock) {
                blocks.push(currentBlock);
            }
            currentBlock = {
                mode: slot.mode.id,
                start: i / 4,
                end: i / 4 + 0.25,
            };
        } else {
            currentBlock.end = (i + 1) / 4;
        }
    }

    if (currentBlock) {
        blocks.push(currentBlock);
    }

    return blocks;
}

export const buildWeeklyFromDaily = schedule => {
    if (!schedule || schedule.weekly_bp || !schedule.daily_bp) {
        return schedule;
    }
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const weekly_bp = {};
    days.forEach(d => {
        weekly_bp[d] = { ...schedule.daily_bp };
    });
    return { ...schedule, weekly_bp };
};

export const configFieldMeta = {
    self_consumption: {
        minimum_soc: {
            description:
                'The `Minimum SoC` is the minimum allowed state of charge for self-consumption; ESS will not discharge below this level. A decimal value between 0 and 1.',
            min: 0.0,
            max: 1.0,
            step: 0.01,
        },
        charge_power: {
            description: 'The `Charge Power` is the power the ESS will use to charge during self-consumption mode. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
        discharge_power: {
            description:
                'The `Discharge Power` is the power the ESS will use to discharge during self-consumption mode. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
    },
    peak_shaving: {
        peak_shave_against: {
            description: 'The `Peak Shave against` option determines if the ESS should shave peaks against grid power or local load.',
        },
        discharge_above: {
            description:
                'The `Discharge above` setting specifies the threshold above which the ESS will discharge to reduce grid consumption. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
        discharge_power: {
            description:
                'The `Max Discharge Power` is the maximum power used by the ESS for discharging when above the threshold. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
        charge_below: {
            description:
                'The `Charge below` setting specifies the threshold below which the ESS will charge to increase grid consumption (usually set to 0). A decimal value.',
            min: 0,
            step: 0.5,
        },
        charge_power: {
            description:
                'The `Max Charge Power` is the maximum charging power the ESS will use when below the threshold. A decimal value bigger the 0.',
            min: 0,
            step: 0.5,
        },
        minimum_soc: {
            description:
                'The `Minimum SoC` is the minimum allowed state of charge during peak shaving; ESS will not discharge below this level. A decimal value between 0 and 1.',
            min: 0.0,
            max: 1.0,
            step: 0.01,
        },
    },

    target_soc: {
        target_soc: {
            description:
                'The `Target SoC` is the target state of charge that the ESS will try to achieve and maintain. A decimal value between 0 and 1.',
            min: 0.0,
            max: 1.0,
            step: 0.01,
        },
        max_power: {
            description:
                'The `Max Charge/Discharge Power` is maximum power response that the ESS will use to charge or discharge the battery. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
        lower_deadband: {
            description:
                'The `Lower deadband` is the minimum deviation allowed below the target SoC before corrective action is taken. If zero, default of -5% is used. A decimal value bigger than 0',
            min: 0,
            step: 0.01,
        },
        upper_deadband: {
            description:
                'The `Upper deadband` is the maximum deviation allowed above the target SoC before corrective action is taken. If zero, default of +5% is used. A decimal value bigger than 0',
            min: 0,
            step: 0.01,
        },
    },

    fcr_d: {
        bid_power_up: {
            description: 'The `Bid Power FCR-D Up` is the max discharge power for FCR-D-Up (frequency below 49.9 Hz). A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
        bid_power_down: {
            description:
                'The `Bid Power FCR-D Down` is the max charge power for FCR-D-Down (frequency above 50.1 Hz). A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
    },

    fcr_d_down: {
        bid_power: {
            description: 'The `Bid Power` is the maximum power for downward regulation in FCR-D-Down mode. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
    },

    fcr_d_up: {
        bid_power: {
            description: 'The `Bid Power` is the maximum power for upward regulation in FCR-D-Up mode. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
    },

    fcr_n: {
        bid_power: {
            description:
                'The `Bid Power` is the maximum symmetric power for FCR-N regulation, responding to frequency deviations. A decimal value bigger than 0.',
            min: 0,
            step: 0.5,
        },
    },

    direct_command: {
        intervals: {
            description: 'The `Intervals` define the schedule in terms of start, end, and power_setpoint in JSON format.',
        },
    },
};

export const canonicalModeKey = raw =>
    raw
        ?.toLowerCase()
        .trim()
        .replace(/[\s_-]+/g, '');

const metaByKey = {};
Object.entries(configFieldMeta).forEach(([k, v]) => {
    metaByKey[canonicalModeKey(k)] = v;
});

export const isMode = (name, target) => canonicalModeKey(name) === canonicalModeKey(target);

export const getFieldMeta = name => metaByKey[canonicalModeKey(name)];

export const showComment = mode => {
    const comments = {
        self_consumption:
            'The battery will discharge to fully cover on-site consumption and only charge when there is surplus energy from local solar production. You can define the maximum charging and discharging power under these conditions.',
        target_soc:
            'The battery will charge or discharge to reach the specified state of charge and maintain that level throughout the session. Charging and discharging power is configurable. This mode can be used to prepare the system for upcoming sessions or when charging/discharging is desired due to, for example, high or low spot prices.',
        fcr_d_down:
            'Provides Frequency Containment Reserve for Disturbances by charging the battery when frequency drops. This mode is typically operated by the balance responsible party or trading department, which is required for monetisation via ancillary services.',
        fcr_n: 'Delivers continuous Frequency Containment Reserve for Normal operation, responding symmetrically to frequency deviations. This mode is typically operated by the balance responsible party or trading department, which is required for monetisation via ancillary services.',
        peaks_having:
            'Used to reduce power peaks during a session. You define at what power level peak shaving should begin, the discharge power, and optionally at what level and with what power the battery may charge. Peak shaving should not be confused with demand charge optimisation, although it can support that goal. It is also separate from load balancing.',
        fcr_d: 'Provides full Frequency Containment Reserve for Disturbances, including both upward and downward regulation. This mode is typically operated by the balance responsible party or trading department, which is required for monetisation via ancillary services.',
        fcr_d_up:
            'Provides Frequency Containment Reserve for Disturbances by discharging the battery when frequency rises. This mode is typically operated by the balance responsible party or trading department, which is required for monetisation via ancillary services.',
        direct_command:
            'The battery is scheduled to charge or discharge at a defined power level during the session. Not a real-time control mode—actions are pre-configured based on operational needs.',
        idle: 'In this mode, no actions are taken by the system. The battery remains inactive, and no charging or discharging is initiated during the session.',
    };

    const descriptions = {};
    Object.entries(comments).forEach(([k, v]) => {
        descriptions[canonicalModeKey(k)] = v;
    });

    return descriptions[canonicalModeKey(mode)] || '';
};

export const modeOrder = ['self_consumption', 'peak_shaving', 'target_soc', 'direct_command', 'fcr_d', 'fcr_d_down', 'fcr_d_up', 'fcr_n'];

export default configFieldMeta;
